import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import * as CryptoJS from 'crypto-js';
import { Storage } from '@ionic/storage-angular';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { LoadingController, ModalController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { environment } from '../environments/environment';
import { FajlNezoModalComponent } from './fajl-nezo-modal.component';
import { ervenyesJogkorNezet } from './jogkor-nezet-tarolo';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private _storage: Storage | null = null;

  /** resolves when Ionic Storage is created */
  private ready: Promise<void>;

  public storageActive = false;

  private loading?: HTMLIonLoadingElement;
  // Race-védelem: ha a hideLoading a present() befejezése előtt fut (gyors API),
  // az Ionic eldobná a dismiss-t és a spinner örökké pörögne. Az id-alapú őr ezt kezeli.
  private loadingId = 0;

  constructor(
    private http: HttpClient,
    private storage: Storage,
    private loadingCtrl: LoadingController,
    private modalCtrl: ModalController
  ) {
    // Kick off storage init immediately so first callers can await it.
    this.ready = this.init();
  }

  // Fájl/PDF megnyitása – KÖZÖS helper. Weben: új fül (window.open, mint eddig). NATÍVON a window.open
  // nem működik (és nem hordozná a tokent), ezért a fájlt blobként letöltjük (az interceptorok ráteszik a
  // tokent + az apiOrigin-t a /api URL-re) és egy beépített nézőben (FajlNezoModalComponent) mutatjuk.
  /*  A kiszolgáló publikus útvonalai – ezekhez nincs token, és nem is kell.
   *  A lista az `auth-middleware.js` PUBLIC_PREFIXES-ét követi; ami nincs benne, az védett. */
  private static readonly PUBLIKUS_UTAK = [
    '/file', '/medfile', '/checkout', '/simplepay', '/track', '/parsemail', '/geninvoice',
    '/tanevrendje/public', '/tanevrendje/embed',
  ];

  /** A tokennel letöltendő cím (a relatív `/api/…` alak), vagy `null`, ha sima megnyitás kell. */
  private tokentIgenyloCim(url: string): string | null {
    const m = String(url || '').match(/\/api\/.*$/);
    if (!m) return null;                       // külső cím vagy nem a saját API – marad a régi út
    const ut = m[0].replace(/^\/api/, '');
    const publikus = DataService.PUBLIKUS_UTAK.some((p) =>
      ut === p || ut.startsWith(`${p}/`) || ut.startsWith(`${p}?`));
    return publikus ? null : m[0];
  }

  async megnyitFajl(url: string, cim: string = 'Dokumentum'): Promise<void> {
    if (!url) return;
    if (!Capacitor.isNativePlatform()) {
      /*  A KISZOLGÁLÓ TOKEN NÉLKÜL NEM AD KI FÁJLT – egy sima `window.open` pedig nem visz
       *  tokent. Ezeknél a fájlt a tokennel töltjük le, és a letöltött tartalmat nyitjuk meg
       *  új lapon. A lapot a kattintás pillanatában nyitjuk meg (üresen), különben a böngésző
       *  előugró ablakként letiltaná.
       *
       *  EZ A SZABÁLY EGY ÉLES KIESÉSBŐL SZÁRMAZIK (2026-09-23). Korábban csak a Könyvelés és
       *  a Bérszámfejtés útvonalait soroltuk ide, mert máshol a kiszolgáló token nélkül is
       *  kiadta a fájlt – csakhogy épp ez volt a biztonsági hiba, amit bezártunk. A bezárás
       *  után az iratok, csatolmányok és riportok megnyitása „csak az iskola munkatársa érheti
       *  el” hibára futott, mert a linkes megnyitás sosem vitt tokent.
       *
       *  MOSTANTÓL FORDÍTVA: minden saját `/api/` cím tokennel megy, KIVÉVE a szándékosan
       *  publikus útvonalakat (`auth-middleware.js` PUBLIC_PREFIXES). Azokat nem is szabad
       *  blobként kezelni: a fizetőoldal például olyan HTML, amire NAVIGÁLNI kell. */
      const tokenes = this.tokentIgenyloCim(url);
      if (!tokenes) { try { window.open(url, '_blank'); } catch { /* ignore */ } return; }
      const lap = window.open('', '_blank');
      try {
        const valasz = await firstValueFrom(this.http.get(tokenes, { responseType: 'blob', observe: 'response' }));
        let blob = valasz.body as Blob;
        let tipus = String(blob?.type || valasz.headers.get('Content-Type') || '');
        /*  Ha a kiszolgáló nem mondta meg a típust, a böngésző találgat – és a PDF-ből
         *  „XML-hiba" oldal lesz. Ilyenkor a tartalom első bájtjaiból ismerjük fel. */
        if (!tipus || tipus === 'application/octet-stream') {
          const fej = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
          const jel = String.fromCharCode(...fej);
          if (jel.startsWith('%PDF')) tipus = 'application/pdf';
          else if (fej[0] === 0x89 && jel.slice(1, 4) === 'PNG') tipus = 'image/png';
          else if (fej[0] === 0xff && fej[1] === 0xd8) tipus = 'image/jpeg';
          if (tipus) blob = new Blob([blob], { type: tipus });
        }
        const objUrl = URL.createObjectURL(blob);
        const cd = valasz.headers.get('Content-Disposition') || '';
        /*  A SZÖVEGES FÁJL LETÖLTÉS, HA A KISZOLGÁLÓ ANNAK SZÁNTA.
         *  A CSV-kimutatás (text/csv) eddig a böngészőben nyílt meg nyers szövegként,
         *  holott épp az a dolga, hogy Excelben megnyitható fájl legyen.
         *  A PDF és a kép SZÁNDÉKOSAN kimarad a szabályból: van olyan végpontunk, ami
         *  megtekintésre szánt PDF-et is „attachment"-tel küld (doksigenerátor-előnézet),
         *  és azt nem akarjuk letöltéssé változtatni. */
        const letoltendo = /(^|;)\s*attachment\b/i.test(cd) && /^text\//i.test(tipus);
        if (!letoltendo && /pdf|image\/|text\//i.test(tipus)) {
          if (lap) lap.location.href = objUrl; else window.open(objUrl, '_blank');
        } else {
          // Nem megjeleníthető (pl. docx, zip) vagy kifejezetten letöltendő: az eredeti fájlnévvel.
          lap?.close();
          const nev = decodeURIComponent((cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i) || [])[1] || cim || 'fajl');
          const a = document.createElement('a');
          a.href = objUrl; a.download = nev; document.body.appendChild(a); a.click(); a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
      } catch (e: any) {
        lap?.close();
        /*  A kiszolgáló üzenete (blob-válaszban JSON) többet mond az általánosnál – pl. a
         *  számlaképnél: „a fájl hiányzott a tárból, az újraletöltést elindítottuk”. */
        let kiszolgalo: any = null;
        try { if (e?.error instanceof Blob) kiszolgalo = JSON.parse(await e.error.text()); } catch { /* nem JSON */ }
        const uzenet = kiszolgalo?.error ? String(kiszolgalo.error)
          : e?.status === 403 ? 'Ehhez a fájlhoz nincs jogosultságod.'
          : e?.status === 404 ? 'A fájl nem található.' : 'Nem sikerült megnyitni a fájlt.';
        this.sendToast(uzenet, kiszolgalo?.ujraletoltes ? 'warning' : 'error');
      }
      return;
    }
    // A tokenhez a relatív /api forma kell (az auth-interceptor csak arra teszi rá) – abszolút URL-ből is kinyerjük.
    const m = String(url).match(/\/api\/.*$/);
    const fetchUrl = m ? m[0] : url;
    const absUrl = this.absFajlUrl(url);
    try {
      const blob = (await this.http.get(fetchUrl, { responseType: 'blob' }).toPromise()) as Blob;
      const objUrl = URL.createObjectURL(blob);
      const modal = await this.modalCtrl.create({
        component: FajlNezoModalComponent,
        componentProps: { url: objUrl, cim, letoltesUrl: absUrl },
      });
      await modal.present();
    } catch (e) {
      // Fallback: in-app böngésző az abszolút URL-lel (pl. ha a blob-letöltés nem megy).
      try { await Browser.open({ url: absUrl }); } catch { /* ignore */ }
    }
  }
  /*  PDF-RIPORT A KISZOLGÁLÓTÓL (backend/pdf-riport.js, POST /api/riport-pdf).
   *  A kérés a táblázat oszlop/sor JSON-ja (riport-pdf.ts), a válasz a kész PDF – a
   *  munkaidő-elszámolás arculatával. A tokent az interceptor teszi rá. Hibánál dob, a
   *  kiszolgáló üzenetével: a hívó dönt, van-e tartalék (a régi, böngészős jsPDF). */
  async riportPdfBlob(keres: any): Promise<Blob> {
    try {
      const blob = await firstValueFrom(this.http.post('/api/riport-pdf', keres, { responseType: 'blob' }));
      return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
    } catch (e: any) {
      let uzenet = '';
      try { if (e?.error instanceof Blob) uzenet = JSON.parse(await e.error.text())?.error || ''; } catch { /* nem JSON */ }
      throw new Error(uzenet || e?.message || 'A PDF elkészítése nem sikerült.');
    }
  }

  /**
   *  A riport PDF-je: `letolt` – letöltés a megadott fájlnévvel (mint a régi `pdf.save()`),
   *  `megnyit` – új lapon. Natívon a beépített nézőben (FajlNezoModalComponent).
   *  A lapot a kattintás pillanatában nyitjuk meg, különben a böngésző letiltaná.
   */
  async riportPdf(keres: any, fajlnev: string, mod: 'letolt' | 'megnyit' = 'letolt'): Promise<void> {
    const nativ = Capacitor.isNativePlatform();
    const lap = !nativ && mod === 'megnyit' ? window.open('', '_blank') : null;
    let blob: Blob;
    try { blob = await this.riportPdfBlob(keres); } catch (e) { lap?.close(); throw e; }
    const objUrl = URL.createObjectURL(blob);
    if (nativ) {
      const modal = await this.modalCtrl.create({
        component: FajlNezoModalComponent, componentProps: { url: objUrl, cim: fajlnev },
      });
      await modal.present();
      return;
    }
    if (mod === 'megnyit') {
      if (lap) lap.location.href = objUrl; else window.open(objUrl, '_blank');
    } else {
      const a = document.createElement('a');
      a.href = objUrl; a.download = /\.pdf$/i.test(fajlnev) ? fajlnev : `${fajlnev}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
  }

  private absFajlUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) return url;
    if (/^\/?api\//.test(url)) {
      const origin = String((environment as any).apiOrigin || (environment as any).erp || '').replace(/\/+$/, '');
      return origin + (url.startsWith('/') ? url : '/' + url);
    }
    return url;
  }

  /** You can still call this manually; it’s idempotent. */
  async init(): Promise<void> {
    if (this._storage) return;

    // If using drivers: await this.storage.defineDriver(/*...*/);
    const storageInstance = await this.storage.create();
    this._storage = storageInstance;
    this.storageActive = true;
  }

  private async ensureStorageReady(): Promise<void> {
    await this.ready;
    if (!this._storage) {
      // This should not happen, but don’t silently return [] — surface the problem.
      throw new Error('Ionic Storage not initialized');
    }
  }

  // ---------- API calls ----------
  /**
   *  Munkatársak listája.
   *
   *  Alapból csak az AKTÍVAK jönnek: akinek lezárult a jogviszonya, az kikerül a
   *  választókból – ahogy a kiiratkozott tanuló is a névsorokból. Az `osszes = true`
   *  a kilépetteket is visszahozza; ez ott kell, ahol a lista nem választó, hanem
   *  NÉVFELOLDÁS korábbi rekordokhoz (különben a régi óra tanárának a helyén csak
   *  az azonosító maradna), illetve az állomány karbantartásánál.
   */
  /*  KI A KIVÁLASZTOTT GYEREK, HA A TANULÓ MAGA LÉPETT BE?
   *
   *  A gondviselői képernyők (jegyek, órarend, mulasztás) mind ugyanazt a
   *  gyermeklistát kérik le. Ha nem szülő, hanem a TANULÓ lép be, a kiszolgáló
   *  saját magát adja vissza egyetlen sorként, `rokonsag: 'saját'` jelöléssel.
   *
   *  Ilyenkor nincs mit választani: a képernyő rögzítse a nevet, és töltsön be
   *  azonnal. Enélkül a tanuló egy üresnek látszó legördülővel találkozik, és a
   *  képernyő addig nem mutat semmit, amíg ki nem választja saját magát.
   *
   *  Azért itt van, nem három komponensben külön: egy „saját” szövegkonstanst
   *  három helyen tartani előbb-utóbb annyit jelent, hogy az egyik lemarad.
   */
  /*  KI A HATÁLYOS BELÉPŐ – NÉZETVÁLTÁS ALATT IS?
   *
   *  A „Belépés más nevében" az alkalmazás oldalán történik: a kiszolgáló felé
   *  továbbra is az adminisztrátor tokene megy, csak a felület viselkedik úgy,
   *  mintha a cél-felhasználó lenne bejelentkezve. A fejléc (AppComponent) ezt
   *  el is végzi a SAJÁT másolatán – a képernyők viszont közvetlenül a belépési
   *  tokenből olvasták a nevet, és így az ADMINISZTRÁTOR nevével kérdeztek.
   *
   *  Ezért látszott üresnek a gyermek-választó egy tanuló nevében belépve: a
   *  gyermeklistát az adminisztrátor azonosítójára kérdeztük, akihez nincs gyerek.
   *
   *  Minden képernyő ezen keresztül vegye a kilétét. Egy helyen dől el, ki
   *  „vagyunk" – nem tizenhét helyen külön.
   */
  belepoKilet(claims: any): any {
    const cel = this.impersonalasCel();
    if (cel) return { ...(claims || {}), username: cel, preferred_username: cel };

    /*  BELÉPÉS-BEKÖTÉS: a belépési név és a nyilvántartásbeli azonosító nem mindig
     *  ugyanaz. A gondviselő törzsadata például az EGYIK gyereke azonosítóján áll, a
     *  belépési fiókja viszont a MÁSIKÉN – ugyanaz az ember, két néven. A kiszolgáló
     *  ilyenkor bekötötte a kettőt; innentől a nyilvántartásbeli azonosítóval
     *  dolgozunk, különben a szülő belépne, de a saját gyerekét nem találná. */
    const bekotott = this.bekotesAzonosito(claims);
    if (bekotott) return { ...(claims || {}), username: bekotott, preferred_username: bekotott };

    return claims;
  }

  /**
   *  A bekötött azonosító – CSAK ahhoz a belépési névhez, amelyikre készült.
   *
   *  A névhez kötés nem formaság: enélkül egy másik felhasználó ugyanezen a gépen
   *  az előző ember azonosítóját örökölné meg, és az ő adatait kérné le.
   */
  bekotesAzonosito(claims: any): string | null {
    try {
      const nyers = localStorage.getItem('cronom_bekotes');
      if (!nyers) return null;
      const b = JSON.parse(nyers);
      const nev = String(claims?.preferred_username || claims?.username || '').trim().toLowerCase();
      return b?.azonosito && nev && String(b.nev || '').toLowerCase() === nev ? String(b.azonosito) : null;
    } catch (e) {
      return null;
    }
  }

  /** A nézetváltás célja, ha épp más nevében vagyunk – különben null. */
  impersonalasCel(): string | null {
    try {
      const nyers = localStorage.getItem('cronom_impersonalas');
      const imp = nyers ? JSON.parse(nyers) : null;
      return imp?.cel ? String(imp.cel) : null;
    } catch (e) {
      return null;
    }
  }

  sajatTanulo(gyerekek: any[]): any | null {
    const lista = Array.isArray(gyerekek) ? gyerekek : [];
    return lista.find((g: any) => String(g?.rokonsag || '').toLowerCase() === 'saját') || null;
  }

  getAllomany(intezmeny = '', osszes = false) {
    return this.http
      .get<any>('/api/nyilvantartas/allomany/' + intezmeny + (osszes ? '?osszes=1' : ''))
      .pipe(map(rsp => rsp));
  }

  getBlokk(datum, ora, intezmeny) {
    return this.http.get<any>('/api/orarend/kizar/' + datum + '/' + ora + '/' + intezmeny).pipe(map(rsp => rsp));
  }

  getTtf(ev, intezmeny) {
    return this.http.get<any>('/api/ttf/' + ev + '/' + intezmeny).pipe(map(rsp => rsp));
  }

  getTtfStatus(ev, intezmeny) {
    return this.http.get<any>('/api/ttf/status/' + ev + '/' + intezmeny).pipe(map(rsp => rsp));
  }

  getCsengetes(ev, intezmeny) {
    return this.http.get<any>('/api/csengetesi_rend/' + ev + '/' + intezmeny).pipe(map(rsp => rsp));
  }

  getOsztaly(ev, intezmeny) {
    return this.http.get<any>('/api/nyilvantartas/osztaly/' + ev + '/' + intezmeny).pipe(map(rsp => rsp));
  }

  getTanterem(ev, intezmeny) {
    return this.http.get<any>('/api/nyilvantartas/tanterem/' + ev + '/' + intezmeny).pipe(map(rsp => rsp));
  }

  getTantargy(ev, intezmeny) {
    return this.http.get<any>('/api/nyilvantartas/tantargy/' + ev + '/' + intezmeny).pipe(map(rsp => rsp));
  }

  getOrarend(ev, intezmeny) {
    return this.http.get<any>('/api/orarend/' + ev + '/' + intezmeny).pipe(map(rsp => rsp));
  }

  getTanusitvanyok(intezmeny) {
    return this.http.get<any>('/api/nyilvantartas/tanusitvanyok/' + intezmeny).pipe(map(rsp => rsp));
  }

  setTanusitvany(intezmeny, id) {
    return this.http.post<any>('/api/nyilvantartas/tanusitvanyok/' + intezmeny, { id }).pipe(map(rsp => rsp));
  }

  letoltTanusitvany(intezmeny, id) {
    return this.http.get('/api/nyilvantartas/tanusitvany-letoltes/' + id + '/' + intezmeny, { responseType: 'blob' });
  }

  deleteTanusitvany(id) {
    return this.http.request<any>('delete', '/api/nyilvantartas/tanusitvanyok/' + id, { body: { id } }).pipe(map(rsp => rsp));
  }

  // Az S/MIME e-mail-aláíró tanúsítvány kijelölése (id nélkül → aláírás kikapcsolása).
  setSmimeSigner(id) {
    return this.http.post<any>('/api/nyilvantartas/smime-signer', { id }).pipe(map(rsp => rsp));
  }
  getSmimeSigner() {
    return this.http.get<any>('/api/nyilvantartas/smime-signer').pipe(map(rsp => rsp));
  }

  // Digitális aláírás beállítása: RFC 3161 időbélyeg (TSA) be/ki + cím.
  getAlairasBeallitas() {
    return this.http.get<any>('/api/nyilvantartas/alairas-beallitas').pipe(map(rsp => rsp));
  }
  setAlairasBeallitas(tsa_enabled, tsa_url) {
    return this.http.post<any>('/api/nyilvantartas/alairas-beallitas', { tsa_enabled, tsa_url }).pipe(map(rsp => rsp));
  }

  // Intézmény-beállítás: feladatellátási hely neve, OM, intézményvezető neve.
  getIntezmenyBeallitas(intezmeny) {
    return this.http.get<any>('/api/nyilvantartas/intezmeny-beallitas/' + intezmeny).pipe(map(rsp => rsp));
  }
  setIntezmenyvezeto(intezmeny, intezmenyvezeto) {
    return this.http.post<any>('/api/nyilvantartas/intezmeny-beallitas/' + intezmeny, { intezmenyvezeto }).pipe(map(rsp => rsp));
  }
  /** A munkábajárás térítésének intézményi díja (feladatellatas.benzinpenz, Ft/km). Csak ezt a
   *  mezőt küldi: a kiszolgáló a kérésből hiányzó intézményvezetőt nem írja felül. */
  setMunkabajarasDij(intezmeny, benzinpenz) {
    return this.http.post<any>('/api/nyilvantartas/intezmeny-beallitas/' + intezmeny, { benzinpenz }).pipe(map(rsp => rsp));
  }

  getNav(intezmeny) {
    return this.http.get<any>('/api/nyilvantartas/nav/' + intezmeny).pipe(map(rsp => rsp));
  }

  getKretaDiff(params: {
    futas_azonosito?: string;
    tipus?: string;
    statusz?: string;
    kereses?: string;
    datum_tol?: string;
    datum_ig?: string;
    page?: number;
    pageSize?: number;
    csak_dontetlen?: boolean;
    irany?: string;
  }) {
    const query = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      query.set(key, typeof value === 'boolean' ? String(value) : String(value));
    });

    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.http.get<any>(`/api/kreta/diff${suffix}`).pipe(map(rsp => rsp));
  }

  saveKretaDiffDecision(body: {
    tipus: string;
    kulcs: string;
    irany: 'kreta_to_rendszer' | 'rendszer_to_kreta' | 'figyelmen_kivul';
    megjegyzes?: string;
    statusz?: string;
  }) {
    return this.http.post<any>('/api/kreta/diff/decision', body).pipe(map(rsp => rsp));
  }

  saveKretaDiffDecisionBulk(body: {
    irany: 'kreta_to_rendszer' | 'rendszer_to_kreta' | 'figyelmen_kivul';
    megjegyzes?: string;
    statusz?: string;
    futas_azonosito?: string;
    tipus?: string;
    statusz_szuro?: string;
    kereses?: string;
    datum_tol?: string;
    datum_ig?: string;
    csak_dontetlen?: boolean;
    szurt_irany?: string;
    items?: Array<{
      tipus: string;
      kulcs: string;
      irany: 'kreta_to_rendszer' | 'rendszer_to_kreta' | 'figyelmen_kivul';
      megjegyzes?: string;
      statusz?: string;
    }>;
  }) {
    const payload = {
      ...body,
      statusz: body.statusz,
      statusz_szuro: body.statusz_szuro,
    };
    return this.http.post<any>('/api/kreta/diff/decision/bulk', payload).pipe(map(rsp => rsp));
  }

  applyKretaDiffDecision(body: {
    tipus: string;
    kulcs: string;
    futas_azonosito?: string;
  }) {
    return this.http.post<any>('/api/kreta/diff/apply', body).pipe(map(rsp => rsp));
  }

  applyKretaDiffDecisionBulk(body: {
    futas_azonosito?: string;
    tipus?: string;
    statusz_szuro?: string;
    kereses?: string;
    datum_tol?: string;
    datum_ig?: string;
    csak_dontetlen?: boolean;
    szurt_irany?: string;
    items?: Array<{
      tipus: string;
      kulcs: string;
    }>;
  }) {
    return this.http.post<any>('/api/kreta/diff/apply/bulk', body).pipe(map(rsp => rsp));
  }

  // Órarend import XLSX letöltése (manuális KRÉTA-importhoz). A teljes választ kérjük, hogy a
  // szerver által adott fájlnevet (Content-Disposition) is használhassuk.
  letoltOrarendImport() {
    return this.http.get('/api/kreta/diff/orarend-import', { responseType: 'blob', observe: 'response' });
  }

  // ---------- Storage helpers (FIXED) ----------
  async getFromStorage(key: string): Promise<any> {
    await this.ensureStorageReady();
    // Ionic Storage returns null when missing
    return this._storage!.get(key);
  }

  async setStorage(key: string, data: any): Promise<void> {
    await this.ensureStorageReady();
    await this._storage!.set(key, data);
  }

  async clearStorage(): Promise<void> {
    await this.ensureStorageReady();
    await this._storage!.clear();
  }

  // ---------- Munkatanév ----------
  // Az intézmény AKTUÁLIS tanéve (feladatellatas.aktualistanev) mellett létezhet egy
  // előkészítés alatt álló tanév is. A "munkatanév" az, amelyiken az adminisztrátor
  // éppen dolgozik (osztályok léptetése, csoportok, TTF, törzsadat). A napló és az
  // értékelés MINDIG az aktuális tanéven marad – ide nem szól bele.
  private munkatanevValtozott = new BehaviorSubject<number | null>(null);
  munkatanev$ = this.munkatanevValtozott.asObservable();

  /** A munkatanév; ha nincs külön beállítva, az intézmény aktuális tanéve. */
  async getMunkatanev(): Promise<number | null> {
    const beallitott = await this.getFromStorage('munkatanev');
    if (beallitott) return Number(beallitott);
    try {
      const t = JSON.parse(await this.getFromStorage('tanev'));
      return t?.aktualistanev != null ? Number(t.aktualistanev) : null;
    } catch (e) {
      return null;
    }
  }

  /** Az intézmény aktuális (megnyitott) tanéve – ettől tér el a munkatanév. */
  async getAktualisTanev(): Promise<number | null> {
    try {
      const t = JSON.parse(await this.getFromStorage('tanev'));
      return t?.aktualistanev != null ? Number(t.aktualistanev) : null;
    } catch (e) {
      return null;
    }
  }

  async setMunkatanev(ev: number | null): Promise<void> {
    if (ev == null) await this.setStorage('munkatanev', null);
    else await this.setStorage('munkatanev', Number(ev));
    this.munkatanevValtozott.next(ev == null ? null : Number(ev));
  }

  getTanevLista(intezmeny: string) {
    return this.http.get<any>('/api/tanev/lista/' + intezmeny);
  }

  // ---------- Alrendszerhez KÖTÖTT menü ----------
  // A kiszámolt bal menüt korábban puszta tömbként tároltuk (`menuLathato`). Az a tömb nem
  // árulta el, MELYIK alrendszerhez tartozik, ezért alrendszer-váltás után a kezdőlap az előző
  // modul menüjét rajzolta ki (pl. FITNESS fejléc alatt a SULINFORM 34 menüpontja). Ezért a
  // menü mostantól a móddal EGYÜTT tárolódik, és aki használja, kötelezően módot ad meg.
  private readonly _lathatoMenu = new BehaviorSubject<{ mod: string; elemek: any[] } | null>(null);
  /** Akkor jelez, amikor az app.component befejezte a menü felépítését (a kezdőlap ekkor frissül). */
  readonly lathatoMenu$ = this._lathatoMenu.asObservable();

  async publikalMenu(mod: string, elemek: any[]): Promise<void> {
    const csomag = { mod: String(mod || ''), elemek: Array.isArray(elemek) ? elemek : [] };
    await this.setStorage('menuLathato', csomag);
    this._lathatoMenu.next(csomag);
  }

  /** A megadott alrendszer menüje, vagy null, ha a tárolt lista MÁS alrendszeré (ill. régi formátumú). */
  async lathatoMenu(mod: string): Promise<any[] | null> {
    const t = await this.getFromStorage('menuLathato');
    if (!t || Array.isArray(t) || !Array.isArray(t.elemek)) return null;
    return t.mod === String(mod || '') ? t.elemek : null;
  }

  // ---------- Your logic ----------
  /*  JOGKÖR-NÉZET. Ha a felhasználó szűkített nézetben van, a menü és a
   *  jogosultság-mátrix is AZZAL az egy jogkörrel töltsön – különben a képernyő
   *  mást mutatna, mint amit a kiszolgáló enged, és a kettő eltérése épp a
   *  legrosszabbkor derülne ki. A kulcsot az interceptor is olvassa. */
  static readonly JOGKOR_NEZET_KULCS = 'cronom_jogkor_nezet';

  jogkorNezet(): any {
    /*  Más nevében belépve az admin SAJÁT nézete nem érvényes: enélkül a menü a
     *  tanuló nevében is az admin nézet-jogkörével töltött (jogkor-nezet-tarolo.ts). */
    return ervenyesJogkorNezet();
  }

  async getJogkor(jogkorok = undefined): Promise<any> {
    const nezet = this.jogkorNezet();
    if (nezet?.jogkor) return nezet.jogkor;
    try {
      const userMetadata = await this.getFromStorage('sajatIntezmeny');
      if (!userMetadata) throw new Error('No userMetadata found in storage');

      const parsedMetadata = userMetadata;
      if (!parsedMetadata) throw new Error('No relevant data found');

      let adat: any;
      const currentMode = await this.getFromStorage('currentMode');
      if (Array.isArray(parsedMetadata.jogkor)) {
        adat = currentMode === 'WEBSHOP'
          && parsedMetadata.jogkor.some(j => ['admin', 'rendszergazda'].includes(j))
          ? ['webshopadmin']
          : parsedMetadata.jogkor;
      } else {
        adat = parsedMetadata.jogkor?.[currentMode];
        if (!adat && currentMode === 'WEBSHOP') {
          const sulinformJogkor = parsedMetadata.jogkor?.SULINFORM || [];
          adat = sulinformJogkor.some(j => ['admin', 'rendszergazda'].includes(j))
            ? ['webshopadmin']
            : sulinformJogkor;
        }
      }

      const legnagyobbjogkor = await this.getLegnagyobbjogkor(adat);
      if (legnagyobbjogkor?.jogkor) return legnagyobbjogkor.jogkor;
      // Fallback: ha a jogkor-rangsor lista (a /api/jogkor/<MODE> válasz) nem tartalmazza a user
      // jogkörét (pl. hiányos szerepkör-konfig egy adott módban), NE 'null'-t adjunk – különben a
      // menü/access URL '/<MODE>/null/...' lesz, a menü üres marad és a váltás beakad. Adjuk vissza
      // a user nyers (első) jogkörét, hogy a backend a tényleges jogkörrel töltsön.
      if (Array.isArray(adat) && adat.length) return adat[0];
      if (typeof adat === 'string' && adat) return adat;
      return null;
    } catch (error) {
      console.error('Error in getJogkor:', error);
      return null;
    }
  }

  async getLegnagyobbjogkor(jk) {
    const jogkorList = await this.getFromStorage('jogkor');
    const jkList = jk;

    let maxJogkor = null;
    let maxSorrend = -10;

    const jkSet = new Set(jkList);

    for (const item of jogkorList) {
      if (jkSet.has(item.jogkor) && item.sorrend != -1 && item.sorrend > maxSorrend) {
        maxSorrend = item.sorrend;
        maxJogkor = item;
      }
    }

    return maxJogkor;
  }

  /**
   *  A bejelentkezett felhasználó hozzáférés-halmaza egyben, hogy egy képernyő ne
   *  indítson külön lekérdezést minden gombra. A `*` mindent enged.
   *
   *  Használat:  const jog = await this.data.hozzaferesek();  ...  jog.szabad('/penzugy/szamla#storno')
   */
  private hozzaferesKeres?: Promise<string[]>;

  async hozzaferesek(): Promise<{ mind: boolean; szabad: (url: string) => boolean }> {
    const lista = await (this.hozzaferesKeres ??= this.hozzaferesLista());
    const mind = lista.includes('*');
    const halmaz = new Set(lista);
    return { mind, szabad: (url: string) => mind || halmaz.has(url) };
  }

  /**
   *  A hozzáférés-lista FRISSEN, a szerverről – oldalbetöltésenként egyszer.
   *
   *  A tárolóban lévő lista a legutóbbi BELÉPÉSKOR készült. Ha közben a Jogosultságok
   *  szerkesztőben változott valami, a régi listával a felhasználó olyan gombokat
   *  veszítene el, amikre már van joga (és fordítva). Ezért lekérjük, és frissítjük a
   *  tárolót is. Hálózati hiba esetén a tárolt lista marad érvényben.
   */
  private async hozzaferesLista(): Promise<string[]> {
    const tarolt = async (): Promise<string[]> => {
      try {
        const a = await this.getFromStorage('access');
        return Array.isArray(a) ? a.map((e: any) => String(e)) : [];
      } catch { return []; }
    };

    try {
      const mode = await this.getFromStorage('currentMode');
      const intezmeny = (await this.getFromStorage('sajatIntezmeny'))?.intezmenyId;
      const jogkor = await this.getJogkor();
      if (!mode || !intezmeny || !jogkor) return tarolt();

      const valasz: any = await firstValueFrom(
        this.http.get(`/api/access/${mode}/${jogkor}/${intezmeny}`));

      // FONTOS: az ÜRES lista is ÉRVÉNYES válasz (a jogkörtől mindent elvettek).
      // Korábban az üres válasz visszaesett a TÁROLT, régi listára, ezért a
      // jogosultság ELVÉTELE sosem lépett életbe – a felhasználó a korábbi jogaival
      // maradt. Csak a hiányzó/hibás választ pótoljuk a tárolóból.
      if (valasz?.url === undefined || valasz?.url === null) return tarolt();
      let lista: any;
      try { lista = JSON.parse(valasz.url || '[]'); } catch { return tarolt(); }
      if (!Array.isArray(lista)) return tarolt();

      await this.setStorage('access', lista);
      return lista.map((e: any) => String(e));
    } catch {
      return tarolt();
    }
  }

  async canIAccess(jk, url) {
    console.log(jk);
    console.log(url);

    const access = await this.getFromStorage('access');
    let szabad = false;

    for (let e of access) {
      if (e === '*') {
        szabad = true;
        break;
      } else if (e === url) {
        szabad = true;
        break;
      }
    }
    return szabad;
  }

  getFaliujsag(intezmeny) {
    const headers = { intezmeny };
    return this.http
      .get<any>('/api/nyilvantartas/faliujsag', { headers })
      .pipe(map(rsp => rsp));
  }

  getMessages(user) {
    const headers = { user };
    return this.http
      .get<any>('/api/messages', { headers })
      .pipe(map(rsp => rsp));
  }

  async getSajatIntezmeny() {
    const userMetadata = await this.getFromStorage('userMetadata');
    return userMetadata?.intezmenyadatok?.filter(d => d.kulso == 0)?.[0] ?? null;
  }

  delOrarend(id) {
    return this.http.post<any>('/api/orarend/' + id, {}).pipe(map(rsp => rsp));
  }

  sendToast(msg, type: SweetAlertIcon = 'success') {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      iconColor: 'white',
      customClass: { popup: 'colored-toast' },
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      }
    });

    Toast.fire({ icon: type, title: msg });
  }

  async showLoading(msg) {
    const myId = ++this.loadingId;
    const loading = await this.loadingCtrl.create({
      message: msg,
      spinner: 'crescent',
      cssClass: 'custom-loading'
    });
    // Időközben jött egy újabb show vagy egy hide -> ezt a példányt eldobjuk
    if (myId !== this.loadingId) {
      try { await loading.dismiss(); } catch {}
      return;
    }
    this.loading = loading;
    await loading.present();
    // Ha present() közben hívták a hideLoading-ot, most zárjuk be
    if (myId !== this.loadingId) {
      try { await loading.dismiss(); } catch {}
      if (this.loading === loading) this.loading = undefined;
    }
  }

  /**
   *  A MÁR látható loader szövegének cseréje – hosszú műveletnél (pl. aSc import) így
   *  látszik, éppen hol tart. Új loadert nem nyit: ha nincs kint, csendben nem csinál
   *  semmit, mert a fázisjelzés nem indíthat el egy elmúlt művelethez tartozó ablakot.
   */
  updateLoading(msg: string) {
    if (this.loading) {
      try { this.loading.message = msg; } catch { }
    }
  }

  async hideLoading() {
    this.loadingId++; // érvénytelenít minden folyamatban lévő show-t
    const loading = this.loading;
    this.loading = undefined;
    if (loading) {
      try {
        await loading.dismiss();
      } catch {
        // ignore if already dismissed / still presenting
      }
    }
  }
}
